import type { EmpleadoResumen, Periodo } from '@/lib/ventas/types';
import { rolEtiqueta } from '@/lib/ventas/periodo';

interface Props {
  open: boolean;
  onClose: () => void;
  empleados: EmpleadoResumen[];
  selectedKey: string;
  onSelectKey: (key: string) => void;
  periodo: Periodo;
  onSelectPeriodo: (periodo: Periodo) => void;
  generando: boolean;
  onDownload: () => void;
}

const periodos: { key: Periodo; label: string }[] = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'semana', label: 'Semana' },
  { key: 'mes', label: 'Mes' },
];

export default function DescargaEmpleadoModal({
  open,
  onClose,
  empleados,
  selectedKey,
  onSelectKey,
  periodo,
  onSelectPeriodo,
  generando,
  onDownload,
}: Props) {
  if (!open) return null;

  const seleccionado = empleados.find((e) => e.key === selectedKey) || empleados[0] || null;
  const hayEmpleados = empleados.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={generando ? undefined : onClose} />

      <div className="relative bg-white rounded-2xl w-full max-w-md animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-user-received-line"></i>
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground-800">Descargar por Empleado</h2>
          </div>
          <button
            onClick={onClose}
            disabled={generando}
            className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="empleadoSel" className="block text-sm font-medium text-foreground-700 mb-2">
              Empleado
            </label>
            <select
              id="empleadoSel"
              value={seleccionado?.key || ''}
              onChange={(e) => onSelectKey(e.target.value)}
              disabled={!hayEmpleados || generando}
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer disabled:cursor-not-allowed disabled:text-foreground-400"
            >
              {hayEmpleados ? (
                empleados.map((e) => (
                  <option key={e.key} value={e.key}>
                    {e.nombre} — {rolEtiqueta(e.rol)} ({e.cantidad} ventas)
                  </option>
                ))
              ) : (
                <option value="">Sin ventas en el período</option>
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">Período</label>
            <div className="inline-flex items-center gap-1 p-1 bg-background-100 rounded-full">
              {periodos.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onSelectPeriodo(p.key)}
                  disabled={generando}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed ${
                    periodo === p.key ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {seleccionado && (
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-800 truncate">{seleccionado.nombre}</p>
                  <p className="text-xs text-foreground-400">
                    {seleccionado.cantidad} ventas en el período
                  </p>
                </div>
                <p className="text-base font-bold text-primary-600 whitespace-nowrap">
                  Bs. {seleccionado.total.toFixed(2)}
                </p>
              </div>
            </div>
          )}

          <button
            onClick={onDownload}
            disabled={!hayEmpleados || generando}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            {generando ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Generando PDF...</span>
              </>
            ) : (
              <>
                <i className="ri-file-pdf-line"></i>
                <span>Descargar PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}